import React, { useRef, useCallback, useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const RESULTS_BG = "https://customer-assets.emergentagent.com/job_elite-fitness-jorge/artifacts/c3d2b1g2_03_coaching_worldgym.jpg";

const TRANSFORMATIONS = [
  { img: "/results/r1.jpg", note: "Hipertrofia · transformación total" },
  { img: "/results/r3.jpg", note: "Definición · plan BOOTY" },
  { img: "/results/r2.jpg", note: "Recomposición · pérdida de grasa" },
  { img: "/results/r5.jpg", note: "Tren superior · fuerza" },
  { img: "/results/r4.jpg", note: "Glúteo · cintura · pierna" },
  { img: "/results/r6.jpg", note: "Espalda · hipertrofia" },
  { img: "/results/r8.jpg", note: "Volumen · masa muscular" },
  { img: "/results/r9.jpg", note: "Recomposición · definición" },
  { img: "/results/r12.jpg", note: "Tren inferior · glúteo" },
  { img: "/results/r7.jpg", note: "Definición · fuerza" },
  { img: "/results/r10.jpg", note: "Contest prep · simetría" },
  { img: "/results/r11.jpg", note: "Definición · masa muscular" },
];

const TESTIMONIALS = [
  {
    quote_es: "Jorge no solo me cambió el cuerpo, me cambió la disciplina. En 4 meses me veía mejor que nunca.",
    quote_en: "Jorge didn't just change my body, he changed my discipline. In 4 months I looked better than ever.",
    name: "Andrea L.",
    role_es: "Cliente Premium · Cancún",
    role_en: "Premium client · Cancún",
  },
  {
    quote_es: "El plan es exigente pero realista. Por primera vez entendí cómo entrenar y comer.",
    quote_en: "The plan is demanding but realistic. For the first time I understood how to train and eat.",
    name: "Marco T.",
    role_es: "Cliente Elite · Madrid",
    role_en: "Elite client · Madrid",
  },
  {
    quote_es: "Mejor inversión del año. Soporte real, resultados reales.",
    quote_en: "Best investment of the year. Real support, real results.",
    name: "Camille B.",
    role_es: "Cliente Essential · Montreal",
    role_en: "Essential client · Montreal",
  },
];

function Lightbox({ index, onClose }) {
  const [current, setCurrent] = useState(index);

  const prev = useCallback(() => setCurrent((c) => (c - 1 + TRANSFORMATIONS.length) % TRANSFORMATIONS.length), []);
  const next = useCallback(() => setCurrent((c) => (c + 1) % TRANSFORMATIONS.length), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, onClose]);

  // Touch swipe support
  const touchStartX = useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
    touchStartX.current = null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 transition"
        aria-label="Cerrar"
      >
        <X size={20} />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs tracking-[0.3em] text-zinc-400 uppercase">
        {current + 1} / {TRANSFORMATIONS.length}
      </div>

      {/* Prev */}
      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-4 z-10 w-12 h-12 flex items-center justify-center bg-black/60 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] transition"
        aria-label="Anterior"
      >
        <ChevronLeft size={24} />
      </button>

      {/* Image */}
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.2 }}
          className="relative max-h-[85vh] max-w-[90vw] flex flex-col items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={TRANSFORMATIONS[current].img}
            alt={`Transformación ${current + 1}`}
            className="max-h-[80vh] max-w-[88vw] object-contain"
          />
          <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-[#FF6B00]">
            {TRANSFORMATIONS[current].note}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Next */}
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-4 z-10 w-12 h-12 flex items-center justify-center bg-black/60 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] transition"
        aria-label="Siguiente"
      >
        <ChevronRight size={24} />
      </button>
    </motion.div>
  );
}

export default function Results() {
  const { t, locale } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
    dragFree: true,
  });

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <section id="results" ref={ref} className="relative py-24 sm:py-32 overflow-hidden" data-testid="results-section">
      <motion.div
        style={{ y }}
        aria-hidden
        className="absolute inset-0 -top-20 -bottom-20 opacity-20"
      >
        <img src={RESULTS_BG} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(100%) brightness(0.4)" }} />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-3xl">
          <div className="text-xs tracking-[0.4em] text-[#FF6B00] uppercase">{t.results.eyebrow}</div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-4 uppercase">{t.results.title}</h2>
          <p className="mt-4 text-zinc-400">{t.results.subtitle}</p>
        </div>

        {/* Carousel */}
        <div className="relative mb-6">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-3 sm:gap-4">
              {TRANSFORMATIONS.map((c, i) => (
                <div
                  key={i}
                  className="flex-none w-[55vw] sm:w-[280px] lg:w-[260px]"
                  data-testid={`transformation-${i}`}
                >
                  <div
                    className="relative aspect-[3/4] overflow-hidden group cursor-pointer"
                    onClick={() => setLightboxIndex(i)}
                  >
                    <img
                      src={c.img}
                      alt={`Transformación cliente Masterfit ${i + 1}`}
                      className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-80" />
                    {/* Zoom hint on hover */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-12 h-12 rounded-full bg-black/60 border border-white/30 flex items-center justify-center">
                        <ZoomIn size={20} className="text-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="text-[9px] tracking-[0.2em] uppercase text-[#FF6B00]">{c.note}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-black/80 border border-white/20 flex items-center justify-center hover:border-[#FF6B00] hover:text-[#FF6B00] transition"
            aria-label="Anterior"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-black/80 border border-white/20 flex items-center justify-center hover:border-[#FF6B00] hover:text-[#FF6B00] transition"
            aria-label="Siguiente"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <p className="text-zinc-600 text-[10px] tracking-[0.2em] uppercase mb-16 text-center">
          ← {locale === "es" ? "desliza para ver más" : "swipe to see more"} →
        </p>

        {/* Testimonials */}
        <div className="grid lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((tes, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="glass-card p-8 lift-card"
              data-testid={`testimonial-${i}`}
            >
              <div className="font-display text-5xl text-[#FF6B00] leading-none">"</div>
              <p className="mt-2 text-zinc-200 leading-relaxed text-base">
                {locale === "es" ? tes.quote_es : tes.quote_en}
              </p>
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="font-bold text-sm tracking-wide">{tes.name}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">
                  {locale === "es" ? tes.role_es : tes.role_en}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox index={lightboxIndex} onClose={() => setLightboxIndex(null)} />
        )}
      </AnimatePresence>
    </section>
  );
}
